import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import WorldFlag from "./lazy-world-flag"; // R17: back on the lazy boundary after fixing the loadWorldFlagsLib interop unwrap.
import { locales, localeDelimiter, namespace } from "@common-config/constants";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { useAppDispatch, useAppSelector } from "@common-reducers/hooks/store.hook";
import { selectCurrentUser } from "@common-reducers/UserSelectors";
import { initializeUserLanguage, changeUserLanguage } from "@common-services/language-detection";

interface LanguageSwitcherProps {
  hideLang?: boolean;
  hideLabel?: boolean;
  value?: string;
  onLanguageChange?: (language: string) => void;
  applyImmediately?: boolean;
}

/**
 * Pure UI component for language selection.
 *
 * Language detection / initialization is handled centrally by
 * `initializeUserLanguage()` in language-detection.ts.
 * This component only:
 *   - Reads the current language from i18n.language
 *   - Handles explicit user selection via the dropdown
 *   - Persists manual selection to localStorage + DB
 */
const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({
  hideLang = false,
  hideLabel = false,
  value,
  onLanguageChange,
  applyImmediately = true,
}) => {
  const compactFlagWidth = 18;
  const compactFlagHeight = 12;
  const regularFlagWidth = 24;
  const regularFlagHeight = 16;

  const { i18n, t } = useTranslation(namespace);
  const dispatch = useAppDispatch();
  const user = useAppSelector(selectCurrentUser);
  const i18nLanguage = (i18n.resolvedLanguage || i18n.language || 'en').split('-')[0].toLowerCase();
  const currentLanguage = (value || i18nLanguage).split('-')[0].toLowerCase();
  const currentLocale = locales.find((locale) => {
    const [lang] = locale.split(localeDelimiter);
    return lang === currentLanguage;
  });
  const [selectedLang = currentLanguage, selectedCountry = "US"] = (currentLocale || `${currentLanguage}-US`).split(localeDelimiter);

  // Initialize language using centralized logic from language-detection service
  // This handles: user preference > WhatsApp language > browser location > English default
  useEffect(() => {
    initializeUserLanguage(user, dispatch);
  }, [user?.preferredLanguage, user?._id, dispatch]);

  const onChangeLanguage = async (value: string) => {
    onLanguageChange?.(value);

    if (!applyImmediately) {
      return;
    }

    // Use centralized function to change language and save preference
    await changeUserLanguage(value, user?._id, dispatch);
  };

  if (hideLabel) {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            style={{ direction: "ltr" }}
            className={cn(
              "h-[36px] w-[56px] min-w-[56px] shrink-0 overflow-hidden rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] px-1 whitespace-nowrap",
              "text-[var(--color-text)] hover:border-[var(--color-primary)]",
              "focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)] focus:ring-offset-0",
              "flex items-center justify-center gap-1"
            )}
          >
            <span
              className="inline-flex h-[12px] w-[18px] shrink-0 overflow-hidden rounded-[2px] border border-black/10"
              style={{
                width: `${compactFlagWidth}px`,
                height: `${compactFlagHeight}px`,
                minWidth: `${compactFlagWidth}px`,
                maxWidth: `${compactFlagWidth}px`,
                minHeight: `${compactFlagHeight}px`,
                maxHeight: `${compactFlagHeight}px`,
              }}
            >
              <WorldFlag
                code={selectedCountry}
                width={compactFlagWidth}
                height={compactFlagHeight}
                style={{
                  width: `${compactFlagWidth}px`,
                  height: `${compactFlagHeight}px`,
                  minWidth: `${compactFlagWidth}px`,
                  maxWidth: `${compactFlagWidth}px`,
                  minHeight: `${compactFlagHeight}px`,
                  maxHeight: `${compactFlagHeight}px`,
                  objectFit: "cover",
                  display: "block",
                  flexShrink: 0
                }}
              />
            </span>
            {!hideLang && (
              <span
                dir="ltr"
                style={{ unicodeBidi: 'isolate' }}
                className="block max-w-[24px] truncate text-[12px] font-semibold uppercase leading-none"
              >
                {selectedLang}
              </span>
            )}
          </button>
        </DropdownMenuTrigger>

        <DropdownMenuContent align="end" sideOffset={6} className="min-w-[110px] p-1">
          {locales.map((locale) => {
            const [lang, country] = locale.split(localeDelimiter);
            const isSelected = lang === currentLanguage;

            return (
              <DropdownMenuItem
                key={lang}
                onSelect={(event) => {
                  event.preventDefault();
                  void onChangeLanguage(lang);
                }}
                className={cn(
                  "cursor-pointer",
                  isSelected && "bg-[var(--color-surface-muted)]"
                )}
              >
                <div className="flex items-center gap-2">
                  <span
                    className="inline-flex h-[12px] w-[18px] shrink-0 overflow-hidden rounded-[2px] border border-black/10"
                    style={{
                      width: `${compactFlagWidth}px`,
                      height: `${compactFlagHeight}px`,
                      minWidth: `${compactFlagWidth}px`,
                      maxWidth: `${compactFlagWidth}px`,
                      minHeight: `${compactFlagHeight}px`,
                      maxHeight: `${compactFlagHeight}px`,
                    }}
                  >
                    <WorldFlag
                      code={country}
                      width={compactFlagWidth}
                      height={compactFlagHeight}
                      style={{
                        width: `${compactFlagWidth}px`,
                        height: `${compactFlagHeight}px`,
                        minWidth: `${compactFlagWidth}px`,
                        maxWidth: `${compactFlagWidth}px`,
                        minHeight: `${compactFlagHeight}px`,
                        maxHeight: `${compactFlagHeight}px`,
                        objectFit: "cover",
                        display: "block",
                        flexShrink: 0
                      }}
                    />
                  </span>
                  <span className="text-sm font-medium uppercase">
                    {lang}
                  </span>
                </div>
              </DropdownMenuItem>
            );
          })}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  return (
    <div>
      {!hideLabel && (
        <span style={{ fontSize: "14px", color: 'var(--color-text)', fontWeight: 500, marginBottom: '8px', display: 'block' }}>{t('accountView.language', 'Language')}</span>
      )}
      <Select
        value={currentLanguage}
        onValueChange={onChangeLanguage}
      >
      <SelectTrigger
        className={cn(
          "h-10 w-full border border-[var(--color-border)] bg-[var(--color-surface)]",
          "hover:border-[var(--color-primary)]",
          "focus:ring-2 focus:ring-[var(--color-primary)] focus:ring-offset-0 focus:border-[var(--color-primary)]",
          "text-[var(--color-text)]",
          hideLang ? "px-3" : "px-3"
        )}
      >
          <div className="flex items-center gap-2">
            <span
              className="inline-flex shrink-0 overflow-hidden rounded-[2px] border border-black/10"
              style={{
                width: `${regularFlagWidth}px`,
                height: `${regularFlagHeight}px`,
                minWidth: `${regularFlagWidth}px`,
                maxWidth: `${regularFlagWidth}px`,
                minHeight: `${regularFlagHeight}px`,
                maxHeight: `${regularFlagHeight}px`,
              }}
            >
              <WorldFlag
                code={selectedCountry}
                width={regularFlagWidth}
                height={regularFlagHeight}
                className="shrink-0"
                style={{
                  width: `${regularFlagWidth}px`,
                  height: `${regularFlagHeight}px`,
                  minWidth: `${regularFlagWidth}px`,
                  maxWidth: `${regularFlagWidth}px`,
                  minHeight: `${regularFlagHeight}px`,
                  maxHeight: `${regularFlagHeight}px`,
                  objectFit: "cover",
                  display: "block",
                  flexShrink: 0
                }}
              />
            </span>
            {!hideLang && (
              <span className="text-sm font-medium uppercase">
                {selectedLang}
              </span>
            )}
          </div>
        </SelectTrigger>
        <SelectContent>
          {locales.map((locale) => {
            const [lang, country] = locale.split(localeDelimiter);
            const isSelected = lang === currentLanguage;

            return (
              <SelectItem
                key={lang}
                value={lang}
                className={cn(
                  "cursor-pointer",
                  isSelected && "bg-[var(--color-surface-muted)]"
                )}
              >
                <div className="flex items-center gap-2">
                  <span
                    className="inline-flex shrink-0 overflow-hidden rounded-[2px] border border-black/10"
                    style={{
                      width: `${regularFlagWidth}px`,
                      height: `${regularFlagHeight}px`,
                      minWidth: `${regularFlagWidth}px`,
                      maxWidth: `${regularFlagWidth}px`,
                      minHeight: `${regularFlagHeight}px`,
                      maxHeight: `${regularFlagHeight}px`,
                    }}
                  >
                    <WorldFlag
                      code={country}
                      width={regularFlagWidth}
                      height={regularFlagHeight}
                      className="shrink-0"
                      style={{
                        width: `${regularFlagWidth}px`,
                        height: `${regularFlagHeight}px`,
                        minWidth: `${regularFlagWidth}px`,
                        maxWidth: `${regularFlagWidth}px`,
                        minHeight: `${regularFlagHeight}px`,
                        maxHeight: `${regularFlagHeight}px`,
                        objectFit: "cover",
                        display: "block",
                        flexShrink: 0
                      }}
                    />
                  </span>
                  <span className="text-sm font-medium uppercase">
                    {lang}
                  </span>
                </div>
              </SelectItem>
            );
          })}
        </SelectContent>
      </Select>
    </div>
  );
};

export default LanguageSwitcher;
